import React, { useState, useEffect } from 'react';
import { JobProvider } from './context/JobContext';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { useJobContext } from './context/JobContext';
import { Toaster, toast } from 'sonner';
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';

import ForgotPassword from './pages/auth/ForgotPassword';
import api from './lib/api';
import './App.css';
import ResetPassword from './pages/auth/ResetPassword';
import InviteAccept from './pages/auth/InviteAccept';
import TwoFactor from './pages/settings/TwoFactor';
import ServiceSetup from './pages/onboarding/ServiceSetup';
import DashboardHome from './pages/dashboard/Home';
import useNetworkStatus from './hooks/useNetworkStatus';
import OfflineBanner from './components/OfflineBanner';
import NetworkGuard from './components/NetworkGuard';
import ErrorBoundary from './components/ErrorBoundary';
import { BillingProvider } from './context/BillingContext';
import TopHeader from './components/TopHeader';
import Sidebar from './components/Sidebar';
import VerifyEmail from './pages/auth/VerifyEmail';
import SearchPage from './pages/SearchPage';
import HistoryPage from './pages/HistoryPage';
import { SkeletonText } from './components/Skeleton';
import useToast from './hooks/useToast';
import ScrollProgress from './components/ScrollProgress';
import OnboardingTour from './components/OnboardingTour';
import Settings from './pages/settings/Settings';
import DPAPage from './pages/settings/DPAPage';
import Billing from './pages/settings/Billing';
import CompanyProfileRoute from './pages/CompanyProfileRoute';
import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboard from './pages/admin/Dashboard';
import AdminUsers from './pages/admin/Users';
import AdminBilling from './pages/admin/AdminBilling';
import AdminUserDetail from './pages/admin/AdminUserDetail';
import AdminDPA from './pages/admin/AdminDPA';
import IntegrationsPage from './pages/settings/IntegrationsPage';
import TeamLayout from './pages/team/TeamLayout';
import TeamPage from './pages/TeamPage';
import InvitationBanner from './components/InvitationBanner';
import SuspendedBanner from './components/SuspendedBanner';
import HelpSupport from './pages/HelpSupport';
import AdminSystemReset from './pages/admin/SystemReset';
import Detections from './pages/admin/Detections';
import SearchDiagnostics from './pages/admin/SearchDiagnostics';
import TokenDiagnostics from './pages/admin/TokenDiagnostics';
import AdminDashboardNew from './pages/admin/AdminDashboardNew';
import ServerHealth from './pages/admin/ServerHealth';
import AuditLogs from './pages/admin/AuditLogs';
import Analytics from './pages/admin/Analytics';
import Expenses from './pages/admin/Expenses';
import ConfigRates from './pages/admin/ConfigRates';
import RateLimits from './pages/admin/RateLimits';
import Operations from './pages/admin/Operations';
import VisitorManifest from './pages/admin/VisitorManifest';
import ComingSoonSubscribers from './pages/admin/ComingSoonSubscribers';
import AdminCryptoPayments from './pages/admin/AdminCryptoPayments';
import AdminRegistrationReview from './pages/admin/AdminRegistrationReview';
import DeletedAccounts from './pages/admin/DeletedAccounts';
import NotFound from './pages/NotFound';
import NotificationsPage from './pages/NotificationsPage';

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  const token = localStorage.getItem('access_token');
  
  if (!token) return <Navigate to="/login" />;

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'var(--bg-primary)',
      }}>
        <div className="loader"></div>
      </div>
    );
  }
  
  if (!user) {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    return <Navigate to="/login" />;
  }
  
  if (user.onboarding_complete === false && window.location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" />;
  }
  
  if (user.onboarding_complete === true && window.location.pathname === '/onboarding') {
    return <Navigate to="/dashboard" />;
  }
  
  return children;
}

function AppContent() {

  const { user, logout, refreshUser } = useAuth();
  const isSdrOrTeamLead = user?.team_role === 'sdr' || user?.team_role === 'team_lead';
  const navigate = useNavigate();
  const location = useLocation();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [latestLeads, setLatestLeads] = useState(null);

  const { isOnline, showBanner } = useNetworkStatus();
  const toast = useToast();

  // Search state,  kept at App level so it persists across navigation
  const [searchProgress, setSearchProgress] = useState('');
  const [pollingQuery, setPollingQuery] = useState('');
  const [hasFailed, setHasFailed] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isConnected, setIsConnected] = useState(false);

  const isSuperAdmin = user?.role === 'super_admin';
  
  const clearCompleted = () => {}; // handled by JobContext
  
  const resetPolling = () => {
    setHasFailed(false);
    setErrorMessage('');
  };
  
  const setExpectedQuery = (query) => {
    setPollingQuery(query);
  };
  

  // Handle window resize for responsive sidebar
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setSidebarOpen(false);
        setSidebarCollapsed(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getActiveTab = () => {
    const path = location.pathname;
    if (path.includes('/new-search')) return 'search';
    if (path.includes('/history')) return 'history';
    if (path.includes('/integrations')) return 'integrations';
    if (path.includes('/help')) return 'help';
    if (path.includes('/settings')) return 'settings';
    if (path.includes('/security')) return 'security';
    if (path.includes('/profile')) return 'profile';
    if (path.includes('/billing')) return 'billing';
    if (path.includes('/team')) return 'team';
    if (path === '/dashboard/admin' || path === '/dashboard/admin/') return 'admin';
    if (path.startsWith('/dashboard/admin/')) return 'admin';
    if (path.startsWith('/dashboard')) return 'dashboard';
    return 'dashboard';
  };

  const activeTab = getActiveTab();

  const handleTabChange = (tab) => {
    const routes = {
      dashboard: '/dashboard',
      search: '/dashboard/new-search',
      history: '/dashboard/history',
      integrations: '/dashboard/integrations',
      team: '/dashboard/team',
      'lead-assignment': '/dashboard/lead-assignment',
      billing: '/dashboard/billing',
      security: '/dashboard/security',
      profile: '/dashboard/profile',
      settings: '/dashboard/settings',
      admin: '/dashboard/admin',
      'admin-audit': '/dashboard/admin',
      'admin-users': '/dashboard/admin/users',
      'admin-billing': '/dashboard/admin/billing',
      'admin-detections': '/dashboard/admin/detections',
      'help': '/dashboard/help',
    };
    navigate(routes[tab] || '/dashboard');
    if (window.innerWidth < 768) setSidebarOpen(false);
  };

  const [showTour, setShowTour] = useState(false);
  
  useEffect(() => {
    const shouldShow = localStorage.getItem('show_onboarding_tour') === 'true';
    if (shouldShow) {
      setShowTour(true);
      localStorage.removeItem('show_onboarding_tour');
    }
  }, []);

  const handleLogout = async () => {
    try { await api.post('/api/auth/logout'); } catch {}
    // handled by JobContext
    // handled by JobContext
    setHasFailed(false);
    setPollingQuery('');
    logout();
    navigate('/login');
  };

  // Check admin status
  useEffect(() => {
    const checkAdmin = async () => {
      try {
        const res = await api.get('/api/auth/me');
        setIsAdmin(res.data.is_admin || res.data.role === 'admin' || res.data.role === 'super_admin');
      } catch { setIsAdmin(false); }
    };
    checkAdmin();
  }, [user]);





  // Mini-banner powered by JobContext
  const { state: jobState } = useJobContext();
  const hasActiveJobs = Object.keys(jobState.activeJobs).length > 0;
  const activeJobArray = Object.values(jobState.activeJobs);
  const showMiniBanner = hasActiveJobs && activeTab !== 'search';

  const sidebarWidth = sidebarCollapsed ? 64 : 300;

  return (
    <div style={{ display: 'flex', height: '100vh', height: '100dvh', background: 'var(--bg-primary)', overflow: 'hidden', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}>
      <ScrollProgress />
      <Sidebar
        isOpen={sidebarOpen}
        isCollapsed={sidebarCollapsed}
        onClose={() => setSidebarOpen(false)}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        isAdmin={isAdmin}
        onLogout={handleLogout}
        isSearching={hasActiveJobs}
        activeJobs={activeJobArray}
      />
      
      {sidebarOpen && window.innerWidth < 768 && (
        <div
          onClick={() => setSidebarOpen(false)}
          style={{
            position: 'fixed', inset: 0,
            background: 'var(--overlay)', zIndex: 600,
          }}
        />
      )}

      <div style={{
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        height: '100%',
      }}>
        <TopHeader
          onToggleSidebar={() => {
            if (window.innerWidth < 768) {
              setSidebarOpen(!sidebarOpen);
            } else {
              setSidebarCollapsed(!sidebarCollapsed);
            }
          }}
          isSidebarOpen={sidebarOpen || !sidebarCollapsed}
        />

        <main style={{
          flex: 1,
          padding: 'clamp(12px, 3vw, 24px)',
          overflowY: 'auto',
          overflowX: 'hidden',
          minWidth: 0,
          maxWidth: '100%',
          WebkitOverflowScrolling: 'touch',
          overscrollBehaviorY: 'contain',
        }}>
          {showBanner && <OfflineBanner onRetry={() => window.location.reload()} />}
          <NetworkGuard isOnline={isOnline}>
            {showTour && (
              <OnboardingTour onComplete={() => setShowTour(false)} />
            )}

            {/* Mini-banner for active search on other pages */}
            {showMiniBanner && activeJobArray.length > 0 && (
              <div style={{
                background: 'linear-gradient(135deg, var(--accent-glow), rgba(163,113,247,0.1))',
                border: '1px solid var(--border-accent)',
                borderRadius: 'var(--radius-md)',
                padding: 'var(--space-sm) var(--space-lg)',
                marginBottom: 'var(--space-lg)',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                gap: 'var(--space-md)', cursor: 'pointer',
              }} onClick={() => navigate('/dashboard/new-search')}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
                  <span style={{ width: 10, height: 10, border: '2px solid var(--accent-primary)', borderTop: '2px solid transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                  <span style={{ color: 'var(--text-primary)', fontSize: 'var(--text-sm)' }}>
                    Searching <strong>"{activeJobArray[0].query || ''}"</strong>,  {activeJobArray[0].progress || 'Processing...'}
                  </span>
                </div>
                <span style={{ color: 'var(--accent-primary)', fontSize: 'var(--text-xs)' }}>View →</span>
              </div>
            )}

            {hasFailed && activeTab !== 'search' && (
              <div style={{
                background: 'linear-gradient(135deg, var(--error-bg), rgba(248,81,73,0.05))',
                border: '1px solid var(--error-bg)',
                borderRadius: 'var(--radius-md)',
                padding: 'var(--space-sm) var(--space-lg)',
                marginBottom: 'var(--space-lg)',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                gap: 'var(--space-md)', cursor: 'pointer',
              }} onClick={() => navigate('/dashboard/new-search')}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
                  <span style={{ fontSize: 16 }}>❌</span>
                  <span style={{ color: 'var(--text-primary)', fontSize: 'var(--text-sm)' }}>
                    Search for <strong>"{pollingQuery}"</strong> failed,  {errorMessage}
                  </span>
                </div>
                <span style={{ color: 'var(--error)', fontSize: 'var(--text-xs)' }}>View →</span>
              </div>
            )}

            <InvitationBanner />
            <SuspendedBanner />

            <Routes>
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<DashboardHome />} />
              <Route path="new-search" element={
                <SearchPage
                  latestLeads={latestLeads}
                  clearLatestLeads={() => setLatestLeads(null)}
                  resetPolling={resetPolling}
                  setExpectedQuery={setExpectedQuery}
                  isConnected={isConnected}
                />
              } />
              <Route path="history" element={<HistoryPage />} />
              <Route path="integrations" element={isSdrOrTeamLead ? <Navigate to="/dashboard" replace /> : <IntegrationsPage />} />
              <Route path="team/*" element={(user?.plan === 'enterprise' && user?.team_status !== 'suspended') ? <TeamPage /> : <Navigate to="/dashboard" replace />} />
              <Route path="help" element={<HelpSupport />} />
              <Route path="security" element={<TwoFactor />} />

              <Route path="settings" element={isSdrOrTeamLead ? <Navigate to="/dashboard" replace /> : <Settings />} />
              <Route path="dpa" element={<DPAPage />} />
              <Route path="billing" element={isSdrOrTeamLead ? <Navigate to="/dashboard" replace /> : <ErrorBoundary message="Failed to load billing page. Please try again."><BillingProvider><Billing /></BillingProvider></ErrorBoundary>} />
              <Route path="company/:companyName" element={<CompanyProfileRoute />} />
              <Route path="notifications" element={<NotificationsPage />} />
              <Route path="onboarding" element={<ServiceSetup />} />
              <Route path="admin" element={isAdmin ? <AdminLayout /> : <AdminLoading />} >
                <Route index element={<AdminDashboardNew />} />
                <Route path="users" element={<AdminUsers />} />
                <Route path="users/:userId" element={<AdminUserDetail />} />
            <Route path="dpa" element={<AdminDPA />} />
                <Route path="billing" element={<AdminBilling />} />
                <Route path="detections" element={<Detections />} />
                <Route path="diagnostics" element={<SearchDiagnostics />} />
                <Route path="tokens" element={<TokenDiagnostics />} />
                <Route path="visitors" element={<VisitorManifest />} />
                <Route path="analytics" element={<Analytics />} />
                <Route path="expenses" element={<Expenses />} />
                <Route path="ratelimits" element={<RateLimits />} />
                <Route path="config" element={<ConfigRates />} />
                <Route path="health" element={<ServerHealth />} />
                <Route path="search-diagnostics" element={<SearchDiagnostics />} />
                <Route path="search-diagnostics" element={<SearchDiagnostics />} />
                <Route path="audit-logs" element={<AuditLogs />} />
                <Route path="operations" element={<Operations />} />
                <Route path="coming-soon-subscribers" element={<ComingSoonSubscribers />} />
                <Route path="crypto-payments" element={<AdminCryptoPayments />} />
                <Route path="registration-review" element={<AdminRegistrationReview />} />
                <Route path="deleted-accounts" element={<DeletedAccounts />} />
                <Route path="reset" element={isSuperAdmin ? <AdminSystemReset /> : <Navigate to="/dashboard/admin" replace />} />
              </Route>
              <Route path="*" element={<Navigate to="dashboard" replace />} />
            </Routes>

          </NetworkGuard>
        </main>
      </div>
    </div>
  );
}

function AdminLoading() {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '60vh',
      background: 'var(--bg-primary)',
    }}>
      <div className="loader"></div>
    </div>
  );
}

function App() {
  return (
    <JobProvider>
      <AppInner />
    </JobProvider>
  );
}

function AppInner() {
  // Handle Google OAuth callback tokens from URL (before any routing)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const accessToken = params.get('access_token');
    const refreshToken = params.get('refresh_token');
    const email = params.get('email');
    
    if (accessToken && refreshToken) {
      localStorage.setItem('access_token', accessToken);
      localStorage.setItem('refresh_token', refreshToken);
      if (email) localStorage.setItem('user_email', email);
      
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
      
      // Reload to trigger auth
      window.location.reload();
    }
  }, []);

  return (
    <Router>
      <AuthProvider>
        <Toaster position="top-right" richColors closeButton />

        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/accept-invite/:token" element={<InviteAccept />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/onboarding" element={<PrivateRoute><ServiceSetup /></PrivateRoute>} />
          <Route path="/dashboard/*" element={<PrivateRoute><AppContent /></PrivateRoute>} />
          <Route path="/" element={<Navigate to="/login" />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </AuthProvider>
    </Router>
  );
}

export default App;